import React, { useState, useEffect, useContext } from 'react';
import {
  X,
  ClipboardList,
  CheckCircle2,
  UtensilsCrossed,
  Package,
  Bike,
  MapPin,
  Clock,
  Phone,
  IndianRupee,
  AlertCircle,
} from 'lucide-react';
import { ThemeContext } from '../../../../context/ThemeContext';

const OrderTrackingModal = ({ order, onClose }) => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  const steps = [
    { id: 1, name: 'Order Placed', key: 'created_at', time: order?.created_at, icon: ClipboardList },
    { id: 2, name: 'Restaurant Accepted', key: 'restro_accept_time', time: order?.restro_accept_time, icon: UtensilsCrossed },
    { id: 3, name: 'Preparing Food', key: 'restro_accept_time', time: order?.restro_accept_time, icon: Package },
    { id: 4, name: 'Ready for Pickup', key: 'restro_ready_time', time: order?.restro_ready_time, icon: CheckCircle2 },
    { id: 5, name: 'Rider Pickup', key: 'rider_pickup_time', time: order?.rider_pickup_time, icon: Bike },
    { id: 6, name: 'Order Delivered', key: 'order_delivered_time', time: order?.order_delivered_time, icon: MapPin },
  ];

  useEffect(() => {
    let stepIndex = 0;
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].time && steps[i].time !== 'null' && steps[i].time !== null) {
        stepIndex = i;
        break;
      }
    }
    setCurrentStepIndex(stepIndex);
  }, [order]);

  useEffect(() => {
    if (order?.restro_accept_time) {
      const acceptedTime = new Date(order.restro_accept_time);
      const estimatedMinutes = order.order_status_driver === 'delivered'
        ? 0
        : order.rider_pickup_time
        ? 15
        : order.restro_ready_time
        ? 25
        : 35;
      const estimated = new Date(acceptedTime.getTime() + estimatedMinutes * 60000);
      setEstimatedTime(estimated);
    }
  }, [order]);

  const progressPercent = ((currentStepIndex + 1) / steps.length) * 100;
  const isDelivered = order?.order_status_driver === 'delivered';
  const isCancelled = order?.order_status_restro === 'cancelled';

  const getVehicleStatus = () => {
    if (isDelivered) return 'Order Delivered Successfully!';
    if (isCancelled) return 'Order Cancelled';
    if (order?.rider_pickup_time) return 'Rider on the way';
    if (order?.restro_ready_time) return 'Waiting for rider pickup';
    if (order?.restro_accept_time) return 'Preparing your order';
    return 'Order being processed';
  };

  // ✅ Close handler with proper function
  const handleClose = () => {
    if (typeof onClose === 'function') {
      onClose();
    }
  };

  // ✅ Click outside to close
  const handleOutsideClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div
      onClick={handleOutsideClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 500,
        background: 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.2s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: theme.surface,
          borderRadius: '24px',
          width: '100%',
          maxWidth: '900px',
          maxHeight: '90vh',
          border: `1px solid ${theme.border}`,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: `0 20px 60px ${theme.primary}30`,
          animation: 'slideUp 0.3s ease-out',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '24px',
            borderBottom: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: theme.surfaceLighter,
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: theme.textPrimary, display: 'flex', alignItems: 'center', gap: '8px' }}>
              📍 Track Your Order
            </h2>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: theme.textMuted }}>
              Order <span style={{ fontFamily: 'monospace', color: theme.primary, fontWeight: 700 }}>#{order?.id}</span> • {order?.receiver_name || `User ${order?.userid}`}
            </p>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: theme.surfaceLighter,
              border: `1px solid ${theme.border}`,
              cursor: 'pointer',
              color: theme.textMuted,
              padding: '8px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = `${theme.error || theme.danger}15`;
              e.currentTarget.style.color = theme.error || theme.danger;
              e.currentTarget.style.borderColor = theme.error || theme.danger;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.surfaceLighter;
              e.currentTarget.style.color = theme.textMuted;
              e.currentTarget.style.borderColor = theme.border;
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div
          style={{
            padding: '24px',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {/* Progress Bar */}
          <div style={{ marginBottom: '32px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                marginBottom: '12px',
                fontSize: '13px',
                fontWeight: 600,
                color: theme.textPrimary,
              }}
            >
              <span>Order Progress</span>
              <span style={{ 
                color: theme.primary,
                background: `${theme.primary}15`,
                padding: '2px 10px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 700,
              }}>
                {Math.round(progressPercent)}%
              </span>
            </div>
            <div
              style={{
                background: theme.surfaceLighter,
                borderRadius: '12px',
                height: '8px',
                overflow: 'hidden',
                border: `1px solid ${theme.border}`,
              }}
            >
              <div
                style={{
                  width: `${progressPercent}%`,
                  background: `linear-gradient(90deg, ${theme.primary}, ${theme.primary}cc)`,
                  height: '100%',
                  borderRadius: '12px',
                  transition: 'width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  boxShadow: `0 0 8px ${theme.primary}60`,
                }}
              />
            </div>
          </div>

          {/* Order Info Cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px',
              marginBottom: '28px',
            }}
          >
            {/* Restaurant Card */}
            <div
              style={{
                background: theme.surfaceLighter,
                padding: '16px',
                borderRadius: '14px',
                border: `1px solid ${theme.border}`,
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.primary;
                e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '10px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: `${theme.primary}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <UtensilsCrossed size={16} color={theme.primary} />
                </div>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: theme.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Restaurant
                </span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: theme.textPrimary, marginBottom: '4px' }}>
                {order?.designation || 'N/A'}
              </div>
              <div style={{ fontSize: '11px', color: theme.textMuted, fontFamily: 'monospace' }}>
                ID: {order?.designation_id || '-'}
              </div>
            </div>

            {/* Customer Card */}
            <div
              style={{
                background: theme.surfaceLighter,
                padding: '16px',
                borderRadius: '14px',
                border: `1px solid ${theme.border}`,
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.primary;
                e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '10px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: `${theme.primary}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Phone size={16} color={theme.primary} />
                </div>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: theme.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Customer
                </span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: theme.textPrimary, marginBottom: '4px' }}>
                {order?.receiver_name || 'N/A'}
              </div>
              <div style={{ fontSize: '11px', color: theme.textMuted, fontFamily: 'monospace' }}>
                📞 {order?.receiver_phone || 'N/A'}
              </div>
            </div>

            {/* Payment Card */}
            <div
              style={{
                background: theme.surfaceLighter,
                padding: '16px',
                borderRadius: '14px',
                border: `1px solid ${theme.border}`,
                transition: 'all 0.3s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = theme.success;
                e.currentTarget.style.boxShadow = `0 4px 12px ${theme.success}20`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = theme.border;
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '10px',
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    background: `${theme.success}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IndianRupee size={16} color={theme.success} />
                </div>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: theme.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Payment
                </span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: theme.textPrimary, marginBottom: '4px' }}>
                ₹{order?.final_amount || order?.price || 0}
              </div>
              <div style={{ fontSize: '11px', color: theme.textMuted }}>
                {order?.pay_method || 'COD'} •{' '}
                <span style={{ color: theme.success, fontWeight: 600 }}>
                  {order?.pay_status === 'paid' ? 'Paid' : 'Pending'}
                </span>
              </div>
            </div>
          </div>

          {/* Status Section - Active Order */}
          {!isDelivered && !isCancelled && (
            <div
              style={{
                background: `${theme.primary}10`,
                border: `2px solid ${theme.primary}40`,
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '28px',
                animation: 'pulse-border 2s infinite',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                }}
              >
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    background: `linear-gradient(135deg, ${theme.primary}, ${theme.primary}cc)`,
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: 'bikeMove 1.5s ease-in-out infinite',
                    boxShadow: `0 4px 12px ${theme.primary}40`,
                    flexShrink: 0,
                  }}
                >
                  <Bike size={32} color='white' strokeWidth={1.5} />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 700,
                      color: theme.textPrimary,
                    }}
                  >
                    {getVehicleStatus()}
                  </div>
                  <div
                    style={{
                      fontSize: '13px',
                      color: theme.textMuted,
                      marginTop: '4px',
                    }}
                  >
                    {order?.rider_pickup_time
                      ? '🏍️ Rider is on the move'
                      : order?.restro_ready_time
                      ? '⏳ Waiting for rider pickup'
                      : '👨‍🍳 Order being prepared'}
                  </div>
                  {order?.delivery_person_id && (
                    <div
                      style={{
                        fontSize: '11px',
                        color: theme.textMuted,
                        marginTop: '6px',
                        fontFamily: 'monospace',
                      }}
                    >
                      🆔 Rider ID: {order.delivery_person_id}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Cancelled Status */}
          {isCancelled && (
            <div
              style={{
                background: `${theme.error || theme.danger}15`,
                border: `2px solid ${theme.error || theme.danger}40`,
                borderRadius: '16px',
                padding: '20px',
                marginBottom: '28px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  fontSize: '16px',
                  fontWeight: 700,
                  color: theme.error || theme.danger,
                }}
              >
                <AlertCircle size={24} />
                Order Cancelled
              </div>
              {order?.user_cancel_remarks && (
                <div
                  style={{
                    fontSize: '12px',
                    color: theme.textMuted,
                    marginTop: '8px',
                  }}
                >
                  Reason: {order.user_cancel_remarks}
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          <div
            style={{
              marginBottom: '24px',
              overflowX: 'auto',
              paddingBottom: '12px',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: '0',
                minWidth: '100%',
                position: 'relative',
              }}
            >
              {steps.map((step, idx) => {
                const isCompleted = step.time && step.time !== 'null' && step.time !== null;
                const isActive = idx === currentStepIndex && !isCompleted && !isDelivered;
                const isCurrent = idx === currentStepIndex;
                const IconComponent = step.icon;

                return (
                  <div
                    key={step.id}
                    style={{
                      flex: 1,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    {idx < steps.length - 1 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '28px',
                          left: '50%',
                          width: '100%',
                          height: '3px',
                          background: isCompleted
                            ? theme.success
                            : theme.border,
                          zIndex: 0,
                          transition: 'all 0.3s ease',
                        }}
                      />
                    )}

                    <div
                      style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '14px',
                        background: isCompleted
                          ? theme.success
                          : isActive
                          ? theme.primary
                          : theme.surfaceLighter,
                        border: `2px solid ${
                          isCompleted
                            ? theme.success
                            : isActive
                            ? theme.primary
                            : isCurrent
                            ? theme.primary
                            : theme.border
                        }`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        position: 'relative',
                        transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        boxShadow: isCurrent && !isCompleted
                          ? `0 0 0 4px ${theme.primary}25`
                          : isCompleted
                          ? `0 4px 12px ${theme.success}40`
                          : 'none',
                      }}
                    >
                      {isCompleted ? (
                        <CheckCircle2 size={28} color='white' strokeWidth={2.5} />
                      ) : (
                        <IconComponent
                          size={28}
                          color={isActive ? 'white' : theme.textMuted}
                          strokeWidth={1.5}
                        />
                      )}
                    </div>

                    <div
                      style={{
                        fontSize: '12px',
                        fontWeight: isCurrent ? 700 : 600,
                        color: isCurrent ? theme.primary : theme.textPrimary,
                        marginTop: '12px',
                        textAlign: 'center',
                        minHeight: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {step.name}
                    </div>

                    {step.time && step.time !== 'null' && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: theme.textMuted,
                          marginTop: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontFamily: 'monospace',
                          background: `${theme.success}15`,
                          padding: '2px 8px',
                          borderRadius: '8px',
                        }}
                      >
                        <Clock size={10} />
                        {new Date(step.time).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ETA Card */}
          {!isDelivered && !isCancelled && (
            <div
              style={{
                background: `${theme.primary}10`,
                border: `2px solid ${theme.primary}30`,
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  fontSize: '11px',
                  fontWeight: 700,
                  color: theme.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '12px',
                }}
              >
                <Clock size={14} />
                Estimated Delivery
              </div>
              <div
                style={{
                  fontSize: '36px',
                  fontWeight: 700,
                  color: theme.primary,
                  marginBottom: '8px',
                  fontFamily: 'monospace',
                }}
              >
                {estimatedTime
                  ? estimatedTime.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : 'Calculating...'}
              </div>
              <div style={{ fontSize: '12px', color: theme.textMuted }}>
                {order?.rider_pickup_time
                  ? '🏍️ Rider on the way'
                  : order?.restro_ready_time
                  ? '⏳ Waiting for rider'
                  : '👨‍🍳 Being prepared'}
              </div>
            </div>
          )}

          {/* Delivered Card */}
          {isDelivered && (
            <div
              style={{
                background: `${theme.success}15`,
                border: `2px solid ${theme.success}40`,
                borderRadius: '16px',
                padding: '24px',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  background: `${theme.success}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <CheckCircle2 size={36} color={theme.success} />
              </div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: 700,
                  color: theme.success,
                  marginBottom: '8px',
                }}
              >
                ✅ Order Delivered!
              </div>
              {order?.order_delivered_time && (
                <div style={{ fontSize: '12px', color: theme.textMuted }}>
                  Delivered on {new Date(order.order_delivered_time).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            background: theme.surfaceLighter,
          }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: '10px 24px',
              borderRadius: '10px',
              border: `1px solid ${theme.border}`,
              background: 'transparent',
              color: theme.textPrimary,
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme.surface;
              e.currentTarget.style.borderColor = theme.primary;
              e.currentTarget.style.color = theme.primary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = theme.border;
              e.currentTarget.style.color = theme.textPrimary;
            }}
          >
            Close
          </button>
        </div>

        <style>{`
          @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
          @keyframes bikeMove {
            0%, 100% { transform: translateX(0px) rotateZ(0deg); }
            50% { transform: translateX(8px) rotateZ(2deg); }
          }
          @keyframes pulse-border {
            0%, 100% { 
              border-color: ${theme.primary}40;
              box-shadow: 0 0 0 0 ${theme.primary}20;
            }
            50% { 
              border-color: ${theme.primary}60;
              box-shadow: 0 0 0 8px ${theme.primary}10;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrderTrackingModal;